import { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { AddressLookup } from '@/components/address-lookup';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Card,
  CardContent,
} from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Plus, Pencil, Trash2, MapPin, Package, ChevronDown, ChevronRight, MoreVertical } from 'lucide-react';
import { mockLocations } from '@/lib/mock-settings-data';
import { mockAssets } from '@/lib/mock-data';
import type { Location, SubLocation } from '@/types/settings';
import { useMode } from '@/providers/ModeProvider';
import { useGetBackendUrl } from '@/hooks/use-get-backend-url';

export default function LocationsPage(): React.ReactElement {
  const navigate = useNavigate();
  const { isDemo } = useMode();
  const backendUrl = useGetBackendUrl();
  
  const [locations, setLocations] = useState<Location[]>(mockLocations);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSubLocationDialogOpen, setIsSubLocationDialogOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);
  const [editingSubLocation, setEditingSubLocation] = useState<SubLocation | null>(null);
  const [parentLocationForSubLocation, setParentLocationForSubLocation] = useState<Location | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
  const mapIframeRef = useRef<HTMLIFrameElement>(null);
  
  // Expanded locations state
  const [expandedLocations, setExpandedLocations] = useState<Set<string>>(new Set());

  const [formData, setFormData] = useState<Partial<Location>>({
    name: '',
    notes: '',
    address: '',
    locationManager: '',
  });

  const [subLocationFormData, setSubLocationFormData] = useState<Partial<SubLocation>>({
    name: '',
    notes: '',
    locationManager: '',
  });

  // Fetch locations from API in live mode
  useEffect(() => {
    if (!isDemo) {
      fetchLocations();
    }
  }, [isDemo]);

  const fetchLocations = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/settings/locations`);
      if (response.ok) {
        const data = await response.json();
        setLocations(data);
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  // Calculate asset counts dynamically from mockAssets
  const locationsWithAssetCounts = useMemo(() => {
    return locations.map((location) => {
      const assetCount = mockAssets.filter(
        (asset) => asset.location === location.name
      ).length;
      
      // Calculate sub-location asset counts
      const subLocationsWithCounts = (location.subLocations || []).map((subLoc) => ({
        ...subLoc,
        assetCount: mockAssets.filter(
          (asset) => asset.location === location.name && asset.subLocation === subLoc.name
        ).length,
      }));
      
      return {
        ...location,
        assetCount,
        subLocations: subLocationsWithCounts,
      };
    });
  }, [locations]);

  // Select first location by default on mount
  useEffect(() => {
    if (locationsWithAssetCounts.length > 0 && !selectedLocationId) {
      setSelectedLocationId(locationsWithAssetCounts[0].id);
    }
  }, [locationsWithAssetCounts, selectedLocationId]);

  const handleOpenDialog = (location?: Location) => {
    if (location) {
      setEditingLocation(location);
      setFormData(location);
    } else {
      setEditingLocation(null);
      setFormData({
        name: '',
        notes: '',
        address: '',
        locationManager: '',
      });
    }
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!isDemo) {
      try {
        if (editingLocation) {
          // Update existing location
          const response = await fetch(`${backendUrl}/api/settings/locations/${editingLocation.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(formData),
          });
          if (response.ok) {
            await fetchLocations();
          }
        } else {
          // Create new location
          const response = await fetch(`${backendUrl}/api/settings/locations`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(formData),
          });
          if (response.ok) {
            await fetchLocations();
          }
        }
      } catch (error) {
        console.error('Error saving location:', error);
      }
    } else {
      // Demo mode
      if (editingLocation) {
        setLocations(
          locations.map((loc) =>
            loc.id === editingLocation.id
              ? { ...loc, ...formData, modifiedDate: new Date().toISOString() }
              : loc
          )
        );
      } else {
        const newLocation: Location = {
          id: String(locations.length + 1),
          name: formData.name || '',
          notes: formData.notes,
          address: formData.address,
          mapLink: formData.mapLink,
          locationManager: formData.locationManager,
          assetCount: 0,
          createdBy: 'current-user',
          createdDate: new Date().toISOString(),
          subLocations: [],
        };
        setLocations([...locations, newLocation]);
      }
    }
    setIsDialogOpen(false);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this location? This will also delete all sub-locations.')) {
      return;
    }
    
    if (!isDemo) {
      try {
        const response = await fetch(`${backendUrl}/api/settings/locations/${id}`, {
          method: 'DELETE',
        });
        if (response.ok) {
          await fetchLocations();
        }
      } catch (error) {
        console.error('Error deleting location:', error);
      }
    } else {
      setLocations(locations.filter((loc) => loc.id !== id));
    }
  };

  const handleAssetCountClick = (locationName: string, subLocationName?: string) => {
    if (subLocationName) {
      navigate(`/assets?location=${encodeURIComponent(locationName)}&subLocation=${encodeURIComponent(subLocationName)}`);
    } else {
      navigate(`/assets?location=${encodeURIComponent(locationName)}`);
    }
  };

  const toggleLocationExpanded = (locationId: string) => {
    setExpandedLocations((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(locationId)) {
        newSet.delete(locationId);
      } else {
        newSet.add(locationId);
      }
      return newSet;
    });
  };

  // Sub-location handlers
  const handleOpenSubLocationDialog = (parentLocation: Location, subLocation?: SubLocation) => {
    setParentLocationForSubLocation(parentLocation);
    if (subLocation) {
      setEditingSubLocation(subLocation);
      setSubLocationFormData(subLocation);
    } else {
      setEditingSubLocation(null);
      setSubLocationFormData({
        name: '',
        notes: '',
        locationManager: '',
      });
    }
    setIsSubLocationDialogOpen(true);
  };

  const handleSaveSubLocation = async () => {
    if (!parentLocationForSubLocation) return;

    if (!isDemo) {
      try {
        if (editingSubLocation) {
          // Update existing sub-location
          const response = await fetch(`${backendUrl}/api/settings/sublocations/${editingSubLocation.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(subLocationFormData),
          });
          if (response.ok) {
            await fetchLocations();
          }
        } else {
          // Create new sub-location
          const response = await fetch(`${backendUrl}/api/settings/locations/${parentLocationForSubLocation.id}/sublocations`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(subLocationFormData),
          });
          if (response.ok) {
            await fetchLocations();
          }
        }
      } catch (error) {
        console.error('Error saving sub-location:', error);
      }
    } else {
      // Demo mode
      if (editingSubLocation) {
        setLocations(
          locations.map((loc) =>
            loc.id === parentLocationForSubLocation.id
              ? {
                  ...loc,
                  subLocations: (loc.subLocations || []).map((subLoc) =>
                    subLoc.id === editingSubLocation.id
                      ? { ...subLoc, ...subLocationFormData, modifiedDate: new Date().toISOString() }
                      : subLoc
                  ),
                }
              : loc
          )
        );
      } else {
        const newSubLocation: SubLocation = {
          id: `sub-${Date.now()}`,
          name: subLocationFormData.name || '',
          notes: subLocationFormData.notes,
          locationManager: subLocationFormData.locationManager,
          assetCount: 0,
          parentLocationId: parentLocationForSubLocation.id,
          createdBy: 'current-user',
          createdDate: new Date().toISOString(),
        };
        setLocations(
          locations.map((loc) =>
            loc.id === parentLocationForSubLocation.id
              ? { ...loc, subLocations: [...(loc.subLocations || []), newSubLocation] }
              : loc
          )
        );
      }
    }
    
    setIsSubLocationDialogOpen(false);
  };

  const handleDeleteSubLocation = async (parentLocationId: string, subLocationId: string) => {
    if (!confirm('Are you sure you want to delete this sub-location?')) {
      return;
    }

    if (!isDemo) {
      try {
        const response = await fetch(`${backendUrl}/api/settings/sublocations/${subLocationId}`, {
          method: 'DELETE',
        });
        if (response.ok) {
          await fetchLocations();
        }
      } catch (error) {
        console.error('Error deleting sub-location:', error);
      }
    } else {
      setLocations(
        locations.map((loc) =>
          loc.id === parentLocationId
            ? { ...loc, subLocations: (loc.subLocations || []).filter((subLoc) => subLoc.id !== subLocationId) }
            : loc
        )
      );
    }
  };

  // Handlers for inline sub-location management in tabs
  const handleAddSubLocation = () => {
    setEditingSubLocation(null);
    setSubLocationFormData({
      name: '',
      notes: '',
      locationManager: '',
    });
  };

  const handleEditSubLocation = (subLocation: SubLocation) => {
    setEditingSubLocation(subLocation);
    setSubLocationFormData(subLocation);
  };

  const handleCancelSubLocationEdit = () => {
    setEditingSubLocation(null);
    setSubLocationFormData({
      name: '',
      notes: '',
      locationManager: '',
    });
  };

  const handleSaveSubLocationInline = async () => {
    if (!editingLocation) return;

    if (!isDemo) {
      try {
        if (editingSubLocation) {
          // Update existing sub-location
          const response = await fetch(`${backendUrl}/api/settings/sublocations/${editingSubLocation.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(subLocationFormData),
          });
          if (response.ok) {
            await fetchLocations();
            // Update the form data with the new sub-locations
            const updatedLocations = await fetch(`${backendUrl}/api/settings/locations`);
            if (updatedLocations.ok) {
              const data = await updatedLocations.json();
              const updatedLocation = data.find((loc: Location) => loc.id === editingLocation.id);
              if (updatedLocation) {
                setFormData(updatedLocation);
              }
            }
          }
        } else {
          // Create new sub-location
          const response = await fetch(`${backendUrl}/api/settings/locations/${editingLocation.id}/sublocations`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(subLocationFormData),
          });
          if (response.ok) {
            await fetchLocations();
            // Update the form data with the new sub-locations
            const updatedLocations = await fetch(`${backendUrl}/api/settings/locations`);
            if (updatedLocations.ok) {
              const data = await updatedLocations.json();
              const updatedLocation = data.find((loc: Location) => loc.id === editingLocation.id);
              if (updatedLocation) {
                setFormData(updatedLocation);
              }
            }
          }
        }
      } catch (error) {
        console.error('Error saving sub-location:', error);
      }
    } else {
      // Demo mode
      if (editingSubLocation) {
        const updatedSubLocations = (formData.subLocations || []).map((subLoc) =>
          subLoc.id === editingSubLocation.id
            ? { ...subLoc, ...subLocationFormData, modifiedDate: new Date().toISOString() }
            : subLoc
        );
        setFormData({ ...formData, subLocations: updatedSubLocations });
        setLocations(
          locations.map((loc) =>
            loc.id === editingLocation.id
              ? { ...loc, subLocations: updatedSubLocations }
              : loc
          )
        );
      } else {
        const newSubLocation: SubLocation = {
          id: `sub-${Date.now()}`,
          name: subLocationFormData.name || '',
          notes: subLocationFormData.notes,
          locationManager: subLocationFormData.locationManager,
          assetCount: 0,
          parentLocationId: editingLocation.id,
          createdBy: 'current-user',
          createdDate: new Date().toISOString(),
        };
        const updatedSubLocations = [...(formData.subLocations || []), newSubLocation];
        setFormData({ ...formData, subLocations: updatedSubLocations });
        setLocations(
          locations.map((loc) =>
            loc.id === editingLocation.id
              ? { ...loc, subLocations: updatedSubLocations }
              : loc
          )
        );
      }
    }
    
    // Reset the form
    handleCancelSubLocationEdit();
  };

  const handleDeleteSubLocationInline = async (subLocationId: string) => {
    if (!editingLocation) return;
    if (!confirm('Are you sure you want to delete this sub-location?')) {
      return;
    }

    if (!isDemo) {
      try {
        const response = await fetch(`${backendUrl}/api/settings/sublocations/${subLocationId}`, {
          method: 'DELETE',
        });
        if (response.ok) {
          await fetchLocations();
          // Update the form data with the new sub-locations
          const updatedLocations = await fetch(`${backendUrl}/api/settings/locations`);
          if (updatedLocations.ok) {
            const data = await updatedLocations.json();
            const updatedLocation = data.find((loc: Location) => loc.id === editingLocation.id);
            if (updatedLocation) {
              setFormData(updatedLocation);
            }
          }
        }
      } catch (error) {
        console.error('Error deleting sub-location:', error);
      }
    } else {
      const updatedSubLocations = (formData.subLocations || []).filter((subLoc) => subLoc.id !== subLocationId);
      setFormData({ ...formData, subLocations: updatedSubLocations });
      setLocations(
        locations.map((loc) =>
          loc.id === editingLocation.id
            ? { ...loc, subLocations: updatedSubLocations }
            : loc
        )
      );
    }
  };

  const filteredLocations = locationsWithAssetCounts.filter((location) =>
    location.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    location.address?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    location.locationManager?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="border-b bg-card p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <MapPin className="h-6 w-6 text-primary" />
              Locations
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Manage physical locations and sub-locations where assets are stored
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => handleOpenDialog()}>
              <Plus className="mr-2 h-4 w-4" />
              Add Location
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6 space-y-6">
        {/* Search */}
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <Input
              placeholder="Search locations..."
              value={searchQuery}
              onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 flex-1 overflow-hidden">
          {/* Table */}
          <div className="lg:col-span-2 rounded-md border overflow-auto bg-white dark:bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12"></TableHead>
                  <TableHead>Location Name</TableHead>
                  <TableHead>Address</TableHead>
                  <TableHead>Manager</TableHead>
                  <TableHead className="text-center">Assets</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLocations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground">
                      No locations found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredLocations.map((location) => {
                    const isExpanded = expandedLocations.has(location.id);
                    const hasSubLocations = location.subLocations && location.subLocations.length > 0;
                    
                    return (
                      <>
                        {/* Parent Location Row */}
                        <TableRow
                          key={location.id}
                          className={`cursor-pointer transition-colors ${
                            selectedLocationId === location.id ? 'bg-primary/10' : 'hover:bg-muted/50'
                          }`}
                          onClick={() => setSelectedLocationId(location.id)}
                        >
                          <TableCell>
                            {hasSubLocations && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleLocationExpanded(location.id);
                                }}
                              >
                                {isExpanded ? (
                                  <ChevronDown className="h-4 w-4" />
                                ) : (
                                  <ChevronRight className="h-4 w-4" />
                                )}
                              </Button>
                            )}
                          </TableCell>
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              <MapPin className={`h-4 w-4 ${
                                selectedLocationId === location.id ? 'text-primary' : 'text-muted-foreground'
                              }`} />
                              {location.name}
                              {hasSubLocations && (
                                <Badge variant="outline" className="ml-2">
                                  {location.subLocations.length}
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm">{location.address || '-'}</span>
                          </TableCell>
                          <TableCell>{location.locationManager || '-'}</TableCell>
                          <TableCell className="text-center">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAssetCountClick(location.name);
                              }}
                              className="inline-flex items-center gap-1 text-primary hover:underline cursor-pointer"
                            >
                              <Package className="h-3 w-3" />
                              <Badge variant="secondary">{location.assetCount}</Badge>
                            </button>
                          </TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenDialog(location);
                                  }}
                                >
                                  <Pencil className="h-4 w-4 mr-2" />
                                  Edit Location
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenSubLocationDialog(location);
                                  }}
                                >
                                  <Plus className="h-4 w-4 mr-2" />
                                  Add Sub-Location
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(location.id);
                                  }}
                                  className="text-destructive"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete Location
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>

                        {/* Sub-Location Rows (when expanded) */}
                        {isExpanded && hasSubLocations && location.subLocations.map((subLoc, index) => (
                          <TableRow
                            key={`${location.id}-${subLoc.id}`}
                            className="bg-muted/50 hover:bg-muted/70 border-l-4 border-l-primary/30"
                          >
                            <TableCell></TableCell>
                            <TableCell className="font-medium">
                              <div className="flex items-center gap-2 pl-8">
                                <span className="text-muted-foreground">└─</span>
                                {subLoc.name}
                              </div>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm text-muted-foreground">-</span>
                            </TableCell>
                            <TableCell>{subLoc.locationManager || '-'}</TableCell>
                            <TableCell className="text-center">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAssetCountClick(location.name, subLoc.name);
                                }}
                                className="inline-flex items-center gap-1 text-primary hover:underline cursor-pointer"
                              >
                                <Package className="h-3 w-3" />
                                <Badge variant="secondary">{subLoc.assetCount || 0}</Badge>
                              </button>
                            </TableCell>
                            <TableCell className="text-right">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleOpenSubLocationDialog(location, subLoc);
                                    }}
                                  >
                                    <Pencil className="h-4 w-4 mr-2" />
                                    Edit Sub-Location
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteSubLocation(location.id, subLoc.id);
                                    }}
                                    className="text-destructive"
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Delete Sub-Location
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        ))}
                      </>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          {/* Map Card */}
          <Card className="lg:col-span-1 h-full flex flex-col">
            <CardContent className="p-0 flex-1">
              <div className="w-full h-full">
                {locationsWithAssetCounts.length > 0 && locationsWithAssetCounts.some(loc => loc.address) ? (
                  <iframe
                    ref={mapIframeRef}
                    key={selectedLocationId || 'all-locations'}
                    width="100%"
                    height="100%"
                    style={{ border: 0, borderRadius: '8px' }}
                    loading="lazy"
                    allowFullScreen
                    referrerPolicy="no-referrer-when-downgrade"
                    src={
                      selectedLocationId
                        ? (() => {
                            const selectedLoc = locationsWithAssetCounts.find(loc => loc.id === selectedLocationId);
                            return selectedLoc?.address
                              ? `https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${encodeURIComponent(selectedLoc.address)}&zoom=15`
                              : `https://www.google.com/maps/embed/v1/view?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&center=54.5,-4.0&zoom=5`;
                          })()
                        : `https://www.google.com/maps/embed/v1/view?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&center=54.5,-4.0&zoom=5`
                    }
                  />
                ) : (
                  <div className="flex items-center justify-center h-full text-muted-foreground">
                    <div className="text-center">
                      <MapPin className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>No locations with addresses to display</p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Location Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingLocation ? 'Edit Location' : 'Add Location'}
              </DialogTitle>
              <DialogDescription>
                {editingLocation
                  ? 'Update the location details and manage sub-locations.'
                  : 'Enter the details for the new location.'}
              </DialogDescription>
            </DialogHeader>

            <Tabs defaultValue="details" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="details">Location Details</TabsTrigger>
                <TabsTrigger value="sublocations" disabled={!editingLocation}>
                  Sub-Locations
                  {editingLocation && formData.subLocations && formData.subLocations.length > 0 && (
                    <Badge variant="secondary" className="ml-2">
                      {formData.subLocations.length}
                    </Badge>
                  )}
                </TabsTrigger>
              </TabsList>

              {/* Location Details Tab */}
              <TabsContent value="details" className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Location Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    placeholder="e.g., Head Office"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address">Address</Label>
                  <AddressLookup
                    value={formData.address || ''}
                    onChange={(value) =>
                      setFormData({ ...formData, address: value })
                    }
                    placeholder="Start typing to search for an address..."
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="manager">Location Manager</Label>
                  <Input
                    id="manager"
                    value={formData.locationManager}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                      setFormData({ ...formData, locationManager: e.target.value })
                    }
                    placeholder="e.g., John Smith"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                      setFormData({ ...formData, notes: e.target.value })
                    }
                    placeholder="Additional notes about this location"
                    rows={3}
                  />
                </div>
              </TabsContent>

              {/* Sub-Locations Tab */}
              <TabsContent value="sublocations" className="space-y-4 mt-4">
                {editingLocation && (
                  <>
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-medium">Sub-Locations</h3>
                        <p className="text-sm text-muted-foreground">
                          Manage sub-locations for {formData.name}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        onClick={handleAddSubLocation}
                        disabled={!!editingSubLocation}
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add Sub-Location
                      </Button>
                    </div>

                    {/* Sub-Location Form (when adding/editing) */}
                    {(editingSubLocation || subLocationFormData.name !== '') && (
                      <Card className="p-4 border-2 border-primary/20">
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <h4 className="text-sm font-medium">
                              {editingSubLocation ? 'Edit Sub-Location' : 'New Sub-Location'}
                            </h4>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={handleCancelSubLocationEdit}
                            >
                              Cancel
                            </Button>
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="subLocationName">Sub-Location Name *</Label>
                            <Input
                              id="subLocationName"
                              value={subLocationFormData.name}
                              onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                                setSubLocationFormData({ ...subLocationFormData, name: e.target.value })
                              }
                              placeholder="e.g., Floor 2, Building A"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="subLocationManager">Location Manager</Label>
                            <Input
                              id="subLocationManager"
                              value={subLocationFormData.locationManager}
                              onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                                setSubLocationFormData({ ...subLocationFormData, locationManager: e.target.value })
                              }
                              placeholder="e.g., Jane Doe"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="subLocationNotes">Notes</Label>
                            <Textarea
                              id="subLocationNotes"
                              value={subLocationFormData.notes}
                              onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                                setSubLocationFormData({ ...subLocationFormData, notes: e.target.value })
                              }
                              placeholder="Additional notes about this sub-location"
                              rows={2}
                            />
                          </div>

                          <div className="flex justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={handleCancelSubLocationEdit}
                            >
                              Cancel
                            </Button>
                            <Button
                              size="sm"
                              onClick={handleSaveSubLocationInline}
                              disabled={!subLocationFormData.name}
                            >
                              {editingSubLocation ? 'Update' : 'Create'}
                            </Button>
                          </div>
                        </div>
                      </Card>
                    )}

                    {/* Sub-Locations List */}
                    <div className="space-y-2">
                      {formData.subLocations && formData.subLocations.length > 0 ? (
                        <div className="rounded-md border">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Name</TableHead>
                                <TableHead>Manager</TableHead>
                                <TableHead className="text-center">Assets</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {formData.subLocations.map((subLoc) => (
                                <TableRow key={subLoc.id}>
                                  <TableCell className="font-medium">{subLoc.name}</TableCell>
                                  <TableCell>{subLoc.locationManager || '-'}</TableCell>
                                  <TableCell className="text-center">
                                    <button
                                      onClick={() => {
                                        setIsDialogOpen(false);
                                        handleAssetCountClick(formData.name || '', subLoc.name);
                                      }}
                                      className="inline-flex items-center gap-1 text-primary hover:underline cursor-pointer"
                                    >
                                      <Package className="h-3 w-3" />
                                      <Badge variant="secondary">{subLoc.assetCount || 0}</Badge>
                                    </button>
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <div className="flex justify-end gap-2">
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => handleEditSubLocation(subLoc)}
                                        disabled={!!editingSubLocation}
                                      >
                                        <Pencil className="h-4 w-4" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => handleDeleteSubLocationInline(subLoc.id)}
                                        disabled={!!editingSubLocation}
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      ) : (
                        <div className="text-center py-8 text-muted-foreground border rounded-md">
                          <p>No sub-locations yet</p>
                          <p className="text-sm mt-1">Click "Add Sub-Location" to create one</p>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </TabsContent>
            </Tabs>

            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                Close
              </Button>
              <Button onClick={handleSave} disabled={!formData.name}>
                {editingLocation ? 'Update Location' : 'Create Location'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
